import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { RETAILER_TAG_EXTERNAL_REFERENCE_ID } from '@lib/core/retailers/utils/consts';
import { ApiQueries, isApplicationKiosk } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { useKioskRoot } from '@lib/core/service/hooks/useKioskRoot';
import { useScrollToTop } from '@lib/core/service/hooks/useScrollToTop';
import { setIsTasteIdGuideShown } from '@lib/core/service/slices';
import { prependBasename } from '@lib/core/service/utils';
import { useProductList, useProductRate, useUser } from '@lib/core/users/hooks';
import { useFidelityCard } from '@lib/core/users/hooks/useFidelityCard';
import { useUserKiosk } from '@lib/core/users/hooks/useUserKiosk';
import { fetchPromotionProducts, resetPromotionProducts } from '@lib/tools/promotionProducts/slices';
import { RouteUtils } from '@lib/tools/routes';
import { PROMOTION_LABEL_SLUG } from '@lib/tools/shared/helpers/consts';
import { useTypedDispatch } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

import { HeaderFooterKiosk } from '@components/web/src/atoms/HeaderFooter';
import { RootElement } from '@components/web/src/atoms/RootElement/rootElement';

export const HocWrapperKiosk = ({ children }) => {
  useScrollToTop();
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const { retailerLanguages, retailerTags } = useRetailer();

  const { handleResetKioskUserSession } = useKioskRoot();

  const { isUserAuthenticated, userBestMatchCharacterForCurrentServiceProductCategory } = useUser();

  const { isUserKioskAdmin } = useUserKiosk();

  const { productCategory, locale, isTasteIdGuideShown, isHostMocked } = useApp();
  const { fidelityCardId } = useFidelityCard();

  const currentUserCharacterId = userBestMatchCharacterForCurrentServiceProductCategory?.characterId;

  const isHomePage = RouteUtils.currentPageContains(PAGES.vinhood.home);

  const isWelcomePage = RouteUtils.currentPageContains(PAGES.vinhood.welcome);

  const { productRateData, isProductRateDataLoading } = useProductRate();

  const { wishlistProductInstanceIds, isWishlistProductListLoading } = useProductList();

  (function saveExternalReferenceId() {
    const urlParams = new URLSearchParams(window.location.search);
    const externalReferenceIdTag = retailerTags?.[RETAILER_TAG_EXTERNAL_REFERENCE_ID];
    const value = urlParams.get(externalReferenceIdTag);

    if (value) {
      localStorage.setItem(externalReferenceIdTag, value);
    }
  })();

  useEffect(() => {
    if (isApplicationKiosk && fidelityCardId) {
      /** For kiosk users with fidelity cards:
       * Changing loyalty card updates data
       * Changing user character checks availability of promotional products
       * Feature only works if promotional products are available for user's current character */
      dispatch(resetPromotionProducts());

      if (currentUserCharacterId) {
        dispatch(
          fetchPromotionProducts({
            [ApiQueries.character]: currentUserCharacterId,
            [ApiQueries.promotions]: PROMOTION_LABEL_SLUG,
            [ApiQueries.productCategory]: productCategory,
            offset: 0,
          }),
        );
      }
    }
  }, [fidelityCardId, currentUserCharacterId]);

  const shouldShowTasteIdGuide =
    isUserAuthenticated &&
    !isTasteIdGuideShown &&
    [...productRateData, ...wishlistProductInstanceIds].length > 0 &&
    (!isWishlistProductListLoading || !isProductRateDataLoading);

  const handleSetTasteIdPageShown = () =>
    [...productRateData, ...wishlistProductInstanceIds].length > 0 &&
    (!isWishlistProductListLoading || !isProductRateDataLoading) &&
    dispatch(setIsTasteIdGuideShown(true));

  useEffect(() => {
    let timer;
    if (shouldShowTasteIdGuide) {
      timer = setTimeout(handleSetTasteIdPageShown, 10000);
    }
    return () => clearTimeout(timer);
  }, [shouldShowTasteIdGuide]);

  return !isUserKioskAdmin ? (
    <>{children}</>
  ) : (
    <RootElement isKioskVariant isMocked={isHostMocked}>
      <HeaderFooterKiosk
        handleDetachButtonClick={() => handleResetKioskUserSession({ showFidelityMessage: true })}
        handleLogoClick={() => navigate(prependBasename(PAGES.vinhood.home))}
        isShowDetachFidelityCard={!!fidelityCardId}
        isShowLanguageSwitcher={isHomePage}
        locale={locale}
        retailerLanguages={retailerLanguages}
        shouldHideFooter={isWelcomePage}
        shouldHideHeader={isWelcomePage}
      >
        {children}
      </HeaderFooterKiosk>
    </RootElement>
  );
};
